/**
 * Insights: las respuestas de todas las entrevistas puestas una debajo de la
 * otra, por bloque y por pregunta. Es lo que convierte ocho conversaciones
 * sueltas en una lectura del negocio.
 *
 * Un bloque sin respuestas **igual aparece** y dice que está vacío. Esconderlo
 * haría que la falta de dato desaparezca de la pantalla, que es exactamente el
 * problema del sheet original: lo que no se ve, nadie lo carga.
 *
 * Los conteos van siempre con su base ("4 respuestas de 3 entrevistas") y no
 * como porcentaje: un porcentaje sin base no se puede verificar a mano.
 */
import { Link } from 'react-router-dom'

import { formatFechaCorta, plural } from '../../lib/format'
import { rutaCliente } from '../../lib/rutas'
import { Card, Panel, Stack } from '../../ui'
import { Vacio } from '../comunes'
import type { BloqueDeInsights } from './insights'

export interface InsightsPorBloqueProps {
  bloques: readonly BloqueDeInsights[]
  /** Para linkear cada respuesta a la ficha del cliente que la dijo. */
  clientIdPorEntrevista: Readonly<Record<string, string>>
  /** Cuántas entrevistas hay en total, para decir cuántas faltan en un bloque vacío. */
  totalEntrevistas: number
}

export function InsightsPorBloque({
  bloques,
  clientIdPorEntrevista,
  totalEntrevistas,
}: InsightsPorBloqueProps) {
  return (
    <Stack gap="md">
      {bloques.map((bloque) => (
        <Panel
          key={bloque.bloque}
          titulo={`${bloque.bloque} · ${bloque.titulo}`}
          subtitulo={bloque.intencion}
          padding="sm"
          severidad={bloque.respuestas === 0 ? 'warning' : undefined}
          footer={
            bloque.respuestas === 0 ? (
              'Sin una sola respuesta cargada en este bloque.'
            ) : (
              <>
                {bloque.respuestas}{' '}
                {plural(bloque.respuestas, 'respuesta', 'respuestas')} de{' '}
                {bloque.entrevistas}{' '}
                {plural(bloque.entrevistas, 'entrevista', 'entrevistas')} · sobre{' '}
                {totalEntrevistas} planificadas
              </>
            )
          }
        >
          {bloque.respuestas === 0 ? (
            <Vacio
              punteada
              mensaje="Todavía nadie contestó este bloque"
              detalle={`Ninguna de las ${totalEntrevistas} entrevistas tiene respuestas acá. Registralas desde la tabla de arriba.`}
            />
          ) : (
            <Stack gap="md">
              {bloque.preguntas.map((pregunta) => (
                <div
                  key={`${pregunta.bloque}-${pregunta.preguntaNro}`}
                  data-testid={`insight-${pregunta.bloque}${pregunta.preguntaNro}`}
                  className="min-w-0"
                >
                  <h4 className="mb-1.5 flex flex-wrap items-baseline gap-x-2 text-meta text-text">
                    <span className="rotulo shrink-0">
                      {pregunta.bloque}
                      {pregunta.preguntaNro}
                    </span>
                    <span className="min-w-0 flex-1 font-semibold">
                      {pregunta.pregunta}
                    </span>
                    <span className="tabular shrink-0 text-micro font-normal text-muted-strong">
                      {pregunta.respuestas.length}{' '}
                      {plural(
                        pregunta.respuestas.length,
                        'respuesta',
                        'respuestas',
                      )}
                    </span>
                  </h4>

                  <Stack as="ul" gap="xs">
                    {pregunta.respuestas.map((r) => {
                      const clientId = clientIdPorEntrevista[r.interviewId]

                      return (
                        <Card
                          as="li"
                          key={`${r.interviewId}-${pregunta.preguntaNro}`}
                          padding="sm"
                          hundida
                        >
                          <p className="mb-1 flex flex-wrap items-baseline gap-x-2 text-micro text-muted-strong">
                            {clientId === undefined ? (
                              <span className="font-semibold text-text">
                                {r.clientNombre}
                              </span>
                            ) : (
                              <Link
                                to={rutaCliente(clientId)}
                                className="font-semibold text-accent-text underline decoration-from-font underline-offset-2 hover:text-text"
                              >
                                {r.clientNombre}
                                <span className="sr-only">
                                  {' '}
                                  — abrir su ficha en Clientes
                                </span>
                              </Link>
                            )}
                            <span className="tabular">
                              {r.fecha === null
                                ? 'sin fecha'
                                : formatFechaCorta(r.fecha)}
                            </span>
                          </p>
                          <p className="text-meta leading-relaxed text-text">
                            {r.respuesta}
                          </p>
                        </Card>
                      )
                    })}
                  </Stack>
                </div>
              ))}
            </Stack>
          )}
        </Panel>
      ))}
    </Stack>
  )
}
